import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  AuthState,
  PatientState,
  logOut,
  refreshSession,
  selectAuthLoggedIn,
  selectOrbiAgentAvailable,
  selectPatientState,
} from '@patient-ui/patient-web/store';
import { Patient } from '@patient-ui/shared/models';

import { LayoutService } from '../../shared/services/layout.service';

@Component({
  templateUrl: './public-layout.component.html',
  styleUrls: ['./public-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicLayoutComponent implements OnInit, OnDestroy {
  @HostBinding('class') cssClass = 'd-flex flex-column vh-100';
  @ViewChild('ieNotificationModalRef', { static: true })
  ieNotificationModalRef!: TemplateRef<unknown>;
  navigatedFrom: string | undefined;
  destroyed = new Subject<void>();
  patient?: Patient;

  /**
   * Whether the Orbi AI agent is available — the Split flag AND the patient's
   * identity verification (PCSA-13452). Used only for the wrapper's
   * clearance class; the entry point and `patient-ui-orbi-agent-host` gate
   * themselves on the same selector.
   *
   * This shell serves guest pages as well as the signed-in pages the agent links
   * into, and the selector is fail-closed on a patient that is absent or
   * unverified, so nothing agent-related renders on a guest path.
   */
  orbiAgentAvailable$ = this.patientStore.select(selectOrbiAgentAvailable);

  /**
   * Whether this shell is painting the PORTAL header rather than its own public
   * one — which is the only thing that decides how much room the content wrapper
   * has to leave above itself (PCSA-13563).
   *
   * `PublicHeaderComponent` swaps the whole header out on this same signal: with
   * it set, the guest nav is suppressed and `patient-ui-portal-header` renders
   * instead, out of a nested `fixed-top`. That header is TALLER at lg+ — it adds
   * the `d-none d-lg-block` utility bar (Clinical Trials / OnDemand Orders /
   * Purchase a Lab Test / Help Center), 32px on top of the 66px navbar — which is
   * exactly why `PortalLayoutComponent`'s wrapper is `pt-8 pt-lg-9` and not a flat
   * `pt-8`. This shell's wrapper was a flat `mt-8` (64px) at every breakpoint, so
   * on the one route that shows the portal header — Update Insurance, reached from
   * the Billing Agent's response at `/invoices/insurance?navigatedFrom=RU` — the
   * page was given 32px less clearance than the header it was actually under.
   * Measured in Chrome against the built stylesheet: the header's bottom edge sits
   * at 98.1px while the "Back to Billing" breadcrumb's top landed at 96px, i.e.
   * 2.1px BEHIND the header, where every portal page opens 29.9px clear of it.
   *
   * So the clearance follows the header: `mt-lg-9` (96px) while the portal header
   * is up, the original `mt-8` (64px) otherwise. Guest pages of this same shell
   * (landing, registration, guest bill pay) render the short public header and are
   * untouched — as is every breakpoint below lg, where the utility bar is hidden
   * and both headers are the same 66px.
   *
   * Read from the service rather than passed down from the page: the header this
   * layout draws is already decided there, and `setShowHeaderLinks` /
   * `resetDefaults` both clear the flag, so the wrapper cannot get out of step
   * with the header it is clearing.
   */
  showPortalHeader$ = this.layoutService.getShowPortalHeaderLinks();

  /**
   * Whether a patient is signed in on this shell — which arms the idle-timeout
   * modal below (QA 08/23).
   *
   * This shell serves guest pages AND several signed-in ones (Update Insurance
   * from `/invoices/insurance?navigatedFrom=RU`, the rating flow, the pages the
   * AI agent links a patient to), but the modal was rendered only for the RU
   * flow — so a signed-in patient sitting on any other public-layout route was
   * not watched at all. Nothing then noticed their Okta session expiring: the
   * store kept saying `loggedIn`, so the page — and the agent's entry bar with
   * it — went on rendering as signed in until the patient asked something and the
   * gateway rejected the dead token.
   *
   * Guests are untouched: with no session there is nothing to expire, and an
   * anonymous visitor must not be shown "your session is about to end".
   */
  loggedIn$ = this.authstore.select(selectAuthLoggedIn);

  constructor(
    private modalService: NgbModal,
    private authstore: Store<AuthState>,
    private patientStore: Store<PatientState>,
    private layoutService: LayoutService
  ) {}

  ngOnInit(): void {
    if (window.location.href.indexOf('RU') !== -1) {
      this.navigatedFrom = 'RU';
    }
    this.msIeVersion();
    this.patientStore
      .select(selectPatientState)
      .pipe(takeUntil(this.destroyed))
      .subscribe((patientState) => {
        this.patient = patientState.primaryAccountHolder;
      });
  }

  openNotificationModal() {
    return this.modalService.open(this.ieNotificationModalRef, {
      backdrop: 'static',
      keyboard: false,
    });
  }

  msIeVersion(): boolean {
    const ua = window.navigator.userAgent;
    const msie = ua.indexOf('MSIE ');

    // eslint-disable-next-line no-useless-escape
    if (msie > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./)) {
      // If Internet Explorer, return version number
      this.openNotificationModal();
      return true;
    } // If another browser, return 0
    else {
      return false;
    }
    return false;
  }
  handleSession(logoutDecision: boolean) {
    if (logoutDecision) {
      this.layoutService.setSignOutClicked(true);
      this.authstore.dispatch(logOut({ redirect: '/landing' }));
      sessionStorage.clear();
      window.history.replaceState(null, '', '/');
    } else {
      this.authstore.dispatch(refreshSession());
    }
  }
  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
